import { useLazyQuery } from '@apollo/client';
import { loader } from 'graphql.macro';
import { useModal } from '../providers/ModalProvider';
import { useOnlineStatus } from './offline-hooks/offline-misc';
import { modals } from '../providers/modals';

const workspaceObservationTemplateQuery = loader(
  '../graphql/queries/workspace_observation_template.graphql',
);

// Opens the observation modal with the freshest observation template for the
// target workspace, so a template revision published while this session was
// already open (WorkspaceProvider's workspace query is cache-first after its
// initial load) doesn't leave users creating observations against a stale
// question set. Falls back to the already-cached template when offline or
// when the fetch fails, so creating an observation is never blocked on
// network freshness.
export const useOpenObservationModal = () => {
  const { openModal } = useModal();
  const online = useOnlineStatus();
  const [fetchTemplate] = useLazyQuery(workspaceObservationTemplateQuery, {
    fetchPolicy: 'network-only',
  });

  const openObservationModal = ({
    targetWorkspaceId,
    fallbackTemplate,
    variables,
  }) => {
    const open = (template) =>
      openModal({
        modalName: modals.observation,
        variables: {
          ...variables,
          templateId: template?.id,
          questions: template?.categories?.[0]?.questions || [],
        },
      });

    if (!online || !targetWorkspaceId) {
      open(fallbackTemplate);
      return;
    }

    fetchTemplate({ variables: { workspaceId: String(targetWorkspaceId) } })
      .then(({ data, error }) => {
        const fresh = data?.workspaces?.[0]?.observationTemplate;
        open(error || !fresh ? fallbackTemplate : fresh);
      })
      .catch(() => open(fallbackTemplate));
  };

  return { openObservationModal };
};
